import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  FileText, Download, Loader2, Ship, FolderOpen, PoundSterling,
  Archive, FileSpreadsheet, Printer, Send, Shield, Table,
} from 'lucide-react';

const BTN_CLASS = 'border-2 border-amber-500/70 text-amber-400 bg-transparent hover:bg-amber-500/10 active:bg-amber-600 active:text-white active:border-amber-600 h-11 text-sm font-medium';

export const DownloadButtonsSection = ({
  selectedYear, canAddContent, anyBusy,
  generating, generatingZip, generatingHmrc, generatingExcel,
  onGenerateReport, onDownloadZip, onDownloadHmrc, onDownloadExcel, onOpenShare,
}) => {
  const navigate = useNavigate();
  return (
    <div className="pt-4 border-t border-slate-700 space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Button onClick={onGenerateReport} disabled={anyBusy || !canAddContent} variant="outline"
          className={BTN_CLASS} data-testid="generate-report-btn">
          {generating ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generating...</>
          ) : (
            <><Download className="mr-2 h-4 w-4" />Download PDF</>
          )}
        </Button>

        <Button onClick={onDownloadZip} disabled={anyBusy || !canAddContent} variant="outline"
          className={BTN_CLASS} data-testid="download-zip-btn">
          {generatingZip ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</>
          ) : (
            <><Archive className="mr-2 h-4 w-4" />Download ZIP</>
          )}
        </Button>

        <Button onClick={onDownloadHmrc} disabled={anyBusy || !canAddContent} variant="outline"
          className={BTN_CLASS} data-testid="hmrc-package-btn">
          {generatingHmrc ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</>
          ) : (
            <><Shield className="mr-2 h-4 w-4" />HMRC Pack</>
          )}
        </Button>

        <Button onClick={onDownloadExcel} disabled={anyBusy || !canAddContent} variant="outline"
          className={BTN_CLASS} data-testid="download-excel-btn">
          {generatingExcel ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</>
          ) : (
            <><FileSpreadsheet className="mr-2 h-4 w-4" />Download Excel</>
          )}
        </Button>

        <Button onClick={onOpenShare} disabled={anyBusy || !canAddContent} variant="outline"
          className={BTN_CLASS} data-testid="share-accountant-btn">
          <Send className="mr-2 h-4 w-4" />
          Email Accountant
        </Button>

        <Button onClick={() => navigate(`/reports/print?tax_year=${selectedYear}`)} variant="outline"
          className={BTN_CLASS} data-testid="print-view-btn">
          <Printer className="mr-2 h-4 w-4" />
          Print View
        </Button>
      </div>

      {/* Package Contents Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700/50">
          <p className="text-sm font-medium text-white mb-2 flex items-center gap-2">
            <Archive className="h-4 w-4 text-blue-400" />
            Full Package (ZIP)
          </p>
          <div className="grid grid-cols-2 gap-2 text-xs text-slate-400">
            <div className="flex items-center gap-2">
              <FileText className="h-3 w-3 text-amber-400" />
              PDF Tax Summary
            </div>
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="h-3 w-3 text-blue-400" />
              Trips CSV
            </div>
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="h-3 w-3 text-amber-400" />
              Expenses CSV
            </div>
            <div className="flex items-center gap-2">
              <FolderOpen className="h-3 w-3 text-purple-400" />
              All Documents
            </div>
          </div>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-4 border border-emerald-500/20">
          <p className="text-sm font-medium text-white mb-2 flex items-center gap-2">
            <Shield className="h-4 w-4 text-emerald-400" />
            HMRC Report Pack
          </p>
          <div className="grid grid-cols-2 gap-2 text-xs text-slate-400">
            <div className="flex items-center gap-2">
              <Table className="h-3 w-3 text-emerald-400" />
              HS205 Worksheet
            </div>
            <div className="flex items-center gap-2">
              <FileText className="h-3 w-3 text-emerald-400" />
              Claim Cover Letter
            </div>
            <div className="flex items-center gap-2">
              <Ship className="h-3 w-3 text-emerald-400" />
              Trip Schedule
            </div>
            <div className="flex items-center gap-2">
              <PoundSterling className="h-3 w-3 text-emerald-400" />
              Wage Slip Summary
            </div>
          </div>
        </div>
      </div>

      <p className="text-center text-xs text-slate-500">
        All reports follow HMRC HS205 guidelines for Seafarer's Earnings Deduction
      </p>
    </div>
  );
};
